// /api/* route table — the client-side replacement for the desktop Express server.
// Each handler returns { status?, data, headers? }. Unmatched routes return NOT_HANDLED
// so the caller delegates to the real network (where the SW serves /clips,/uploads,…).

import { CONFIG } from '../config.js';
import {
  createAnalysisJob, getAnalysisJob, runAnalysisJob,
  createRenderJob, getRenderJob,
} from './jobs.js';
import { probeMetadata } from './bridge.js';
import { putBlob } from './mediaStore.js';
import { isDemo } from './demo.js';
import { assetService } from './assetService.js';
import { analyzeAsset } from './assets.js';
import { analyzeForClips } from '../lib/engine/analyze.js';
import { analyzeViaLlm } from '../lite/analyzeProxy.js';

export const NOT_HANDLED = Symbol('not-handled');
const VERSION = (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) || '2.3.1';
const ok = (data, status = 200) => ({ status, data });

// Validate a Gemini key cheaply via the public models list (no quota burn).
async function checkGemini(key) {
  if (!key) throw new Error('empty key');
  const r = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(key)}`);
  if (!r.ok) throw new Error(`Gemini key rejected (${r.status})`);
  return true;
}

// Returns NOT_HANDLED, or { status, data }. `req` = { method, path, body, headers, params }.
export async function routeApi(req) {
  const { method, path } = req;
  const body = req.body || {};
  const m = (re) => re.exec(path);

  // ── pipeline: analysis jobs ──────────────────────────────────────────────
  if (method === 'post' && path === '/api/jobs') {
    return ok({ id: createAnalysisJob(body) });
  }
  let g;
  if (method === 'post' && (g = m(/^\/api\/jobs\/([^/]+)\/run$/))) {
    runAnalysisJob(g[1]); // fire-and-forget
    return ok({ ok: true });
  }
  if (method === 'post' && (g = m(/^\/api\/jobs\/([^/]+)\/resume$/))) {
    runAnalysisJob(g[1]);
    return ok({ ok: true });
  }
  if (method === 'get' && (g = m(/^\/api\/jobs\/([^/]+)$/))) {
    const job = getAnalysisJob(g[1]);
    if (!job) return { status: 404, data: { error: 'job not found' } };
    return ok({ status: job.status, phases: job.phases, artifacts: job.artifacts });
  }

  // ── pipeline: render jobs ────────────────────────────────────────────────
  if (method === 'post' && path === '/api/video/process') {
    return ok(createRenderJob(body));
  }
  if (method === 'get' && (g = m(/^\/api\/job\/([^/]+)$/))) {
    const job = getRenderJob(g[1]);
    if (!job) return { status: 404, data: { error: 'job not found' } };
    return ok({ completedClips: job.completedClips, totalClips: job.totalClips, status: job.status });
  }

  // ── key / model checks ───────────────────────────────────────────────────
  if (method === 'post' && path === '/api/system/check-gemini') {
    if (isDemo()) return ok({ ok: true, valid: true, demo: true });
    try { await checkGemini(body.apiKey || req.headers?.['x-gemini-key']); return ok({ ok: true, valid: true }); }
    catch (e) { return { status: 400, data: { error: e.message, valid: false } }; }
  }
  if (method === 'post' && path === '/api/system/check-model') {
    return ok({ ok: true, supported: true });
  }
  if (method === 'get' && path === '/api/gemini/models') {
    const key = req.headers?.['x-gemini-key'];
    try {
      const r = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(key)}`);
      const j = await r.json();
      if (j.error) return { status: r.status === 429 ? 429 : 400, data: { error: j.error.message } };
      const models = (j.models || [])
        // only chat models that can actually run analysis (generateContent); drop
        // embeddings / tts / image / vision-only / aqa / learnlm.
        .filter((mm) => (mm.supportedGenerationMethods || []).includes('generateContent'))
        .map((mm) => mm.name.replace('models/', ''))
        .filter((id) => /^(gemini|gemma)/.test(id) && !/(embedding|aqa|tts|image|vision|imagen|learnlm|live|native-audio|computer)/i.test(id))
        .map((id) => ({
          id,
          displayName: id.replace(/^gemini-/, 'Gemini ').replace(/^gemma-/, 'Gemma ').replace(/-/g, ' ')
            .replace(/\bit\b/, '').replace(/\b\w/g, (c) => c.toUpperCase()).trim(),
          accessible: true,
          recommended: id === 'gemini-3.1-flash-lite',
          tier: /pro/i.test(id) ? 'paid' : 'free',
        }));
      // guarantee a recommended fallback exists
      if (models.length && !models.some((m) => m.recommended)) {
        const flash = models.find((m) => /flash/i.test(m.id)) || models[0];
        if (flash) flash.recommended = true;
      }
      return ok({ models });
    } catch (e) { return { status: 502, data: { error: e.message } }; }
  }

  // ── metadata probe (quality dropdown) ────────────────────────────────────
  if (method === 'get' && path === '/api/youtube/metadata') {
    try { return ok(await probeMetadata(req.params?.url)); }
    catch (e) { return { status: 502, data: { error: e.message } }; }
  }

  // ── Local File / Asset Library: upload → transcribe → analyze (assets.js) ──
  if (method === 'post' && path === '/api/assets/upload') {
    if (!req.file) return { status: 400, data: { error: 'no file' } };
    return ok(await assetService.upload(req.file, { name: body.name }));
  }
  if (method === 'post' && (g = m(/^\/api\/transcribe\/([^/]+)$/)) && g[1] !== 'status') {
    const jobId = assetService.startTranscription(g[1], { language: body.language || 'id' });
    if (!jobId) return { status: 404, data: { error: 'asset not found' } };
    return ok({ jobId });
  }
  if (method === 'get' && (g = m(/^\/api\/transcribe\/status\/([^/]+)$/))) {
    const job = assetService.getJob(g[1]);
    if (!job) return { status: 404, data: { error: 'job not found' } };
    return ok({ status: job.status, progress: job.progress, logs: job.logs, error: job.error });
  }
  if (method === 'get' && (g = m(/^\/api\/transcripts\/([^/]+)$/))) {
    const t = assetService.getTranscript(g[1]);
    if (!t) return { status: 404, data: { error: 'transcript not found' } };
    return ok(t);
  }
  if (method === 'post' && path === '/api/analyze') {
    try {
      return ok(await analyzeAsset(body, {
        assetService, analyzeViaLlm, analyzeForClips,
        geminiKey: req.headers?.['x-gemini-key'], geminiVariant: req.headers?.['x-gemini-variant'],
      }));
    } catch (e) { return { status: 502, data: { error: e.message || String(e) } }; }
  }

  // ── uploads (legacy fallback ingestion path) ────────────────────────────────
  if (method === 'post' && path === '/api/upload') {
    const file = req.file; // populated by the fetch/xhr FormData shim
    if (file) {
      const id = (crypto?.randomUUID?.() || String(Date.now())).slice(0, 18);
      const p = `uploads/${id}-${file.name}`;
      await putBlob('/' + p, file, file.type || 'video/mp4');
      return ok({ id, path: p, filename: file.name });
    }
    return { status: 400, data: { error: 'no file' } };
  }

  // ── load-time endpoints: return UI-expected shapes so nothing white-screens ─
  if (method === 'get' && path === '/api/history') return ok([]);
  if (method === 'get' && path === '/api/assets') return ok(assetService.list());
  if (method === 'get' && path === '/api/logos') return ok([]);
  if (method === 'get' && path === '/api/prompts') return ok([]);
  if (method === 'get' && path === '/api/rss') return ok({ items: [] });
  if (method === 'get' && path === '/api/update-check') return ok({ latest: VERSION, url: '', notes: '' });
  if (method === 'get' && path === '/api/system/encoder-info') return ok({ encoders: ['webcodecs-h264'], default: 'webcodecs-h264' });
  if (method === 'get' && path === '/api/system/ytdlp-status') return ok({ installed: true, version: 'bridge' });
  if (method === 'get' && path === '/api/system/whisper-version') return ok({ version: 'transformers.js whisper-base' });
  if (method === 'get' && path === '/api/system/network-address') return ok({ address: location.host });
  if (method === 'get' && /^\/api\/video\/caption-status\//.test(path)) return ok({ hasSubtitle: false, hasAudio: true });

  // history mutations the UI may fire — no-op success
  if (method === 'delete' && /^\/api\/history\//.test(path)) return ok({ ok: true });
  if (method === 'patch' && /^\/api\/history\//.test(path)) return ok({ ok: true });
  if (method === 'delete' && /^\/api\/assets\//.test(path)) return ok({ ok: true });

  // Any other /api/* — soft 404 (prevents the SPA-fallback-HTML white-screen bug)
  if (path.startsWith('/api/')) return { status: 404, data: { error: `unhandled: ${method} ${path}` } };

  return NOT_HANDLED;
}
